import React, { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { CANNED_ASSESSMENTS } from './creditFormula';

// Per-topic config card. State is owned by the parent so credit calculations
// stay live. A topic config carries: canned assessments, custom questions, and
// selected competitor topic IDs.

function ProjectTopicEditor({
    topic,                          // { id, topic (name), relationship, competitors: [{id, topic, ...}] }
    config,                         // { canned_assessments, custom_questions, competitor_topic_ids }
    onChange,
    onRemove,
}) {
    const competitorOptions = topic.competitors || [];
    const [questionDraft, setQuestionDraft] = useState('');

    const toggleCanned = (id) => {
        const next = config.canned_assessments.includes(id)
            ? config.canned_assessments.filter(x => x !== id)
            : [...config.canned_assessments, id];
        onChange({ ...config, canned_assessments: next });
    };

    const addQuestion = () => {
        const text = questionDraft.trim();
        if (!text) return;
        onChange({ ...config, custom_questions: [...config.custom_questions, text] });
        setQuestionDraft('');
    };

    const removeQuestion = (idx) => {
        onChange({
            ...config,
            custom_questions: config.custom_questions.filter((_, i) => i !== idx),
        });
    };

    const toggleCompetitor = (id) => {
        const next = config.competitor_topic_ids.includes(id)
            ? config.competitor_topic_ids.filter(x => x !== id)
            : [...config.competitor_topic_ids, id];
        onChange({ ...config, competitor_topic_ids: next });
    };

    return (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1"><strong>{topic.topic}</strong></Typography>
                    {topic.relationship && (
                        <Typography variant="caption" color="text.secondary">{topic.relationship}</Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onRemove} title="Remove topic"><DeleteIcon /></IconButton>
            </Box>

            <Typography variant="overline" color="text.secondary">Canned assessments</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                {CANNED_ASSESSMENTS.map(a => (
                    <FormControlLabel
                        key={a.id}
                        control={
                            <Checkbox
                                size="small"
                                checked={config.canned_assessments.includes(a.id)}
                                onChange={() => toggleCanned(a.id)}
                            />
                        }
                        label={<Typography variant="body2">{a.label}</Typography>}
                    />
                ))}
            </Box>

            <Typography variant="overline" color="text.secondary">Custom questions</Typography>
            <Stack spacing={1} sx={{ mb: 1 }}>
                {config.custom_questions.map((q, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{q}</Typography>
                        <IconButton size="small" onClick={() => removeQuestion(idx)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. How crowded is the cruise on weekends?"
                    value={questionDraft}
                    onChange={e => setQuestionDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); } }}
                />
                <Button startIcon={<AddIcon />} onClick={addQuestion} disabled={!questionDraft.trim()}>Add</Button>
            </Box>

            <Typography variant="overline" color="text.secondary">Competitors</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Each competitor counts the same as the base topic toward the review cap.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {competitorOptions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No competitors configured for this topic yet.
                    </Typography>
                )}
                {competitorOptions.map(c => {
                    const selected = config.competitor_topic_ids.includes(c.id);
                    return (
                        <Chip
                            key={c.id}
                            label={c.topic}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => toggleCompetitor(c.id)}
                            size="small"
                        />
                    );
                })}
            </Box>
        </Paper>
    );
}

export default ProjectTopicEditor;
